import { Avatar as BaseAvatar } from '@base-ui/react/avatar';
import type { ComponentProps, ReactNode } from 'react';
import { cx } from '../lib/styled.tsx';

export type AvatarSize = 'sm' | 'md' | 'lg';

const sizes: Record<AvatarSize, string> = {
  sm: 'size-8 text-2xs',
  md: 'size-10 text-sm',
  lg: 'size-14 text-md',
};

export interface AvatarProps extends ComponentProps<typeof BaseAvatar.Root> {
  src?: string;
  alt?: string;
  size?: AvatarSize;
  /** Shown while the image loads and if it fails — initials, usually. */
  fallback?: ReactNode;
}

/**
 * Image with an initials fallback. Base UI keeps the fallback mounted until the
 * image has actually decoded, so the circle never flashes empty.
 *
 * The two states have different grounds, which is what the product does and is
 * worth keeping: an avatar showing a *photograph* sits on neutral
 * `surface-sunken`, so the moment before the image decodes is quiet. The coral
 * tint belongs to the *initials*, where it is the content rather than a
 * placeholder.
 */
export function Avatar({ src, alt, size = 'md', fallback, className, ...props }: AvatarProps) {
  return (
    <BaseAvatar.Root
      className={cx(
        'inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full',
        'align-middle font-semibold',
        src ? 'bg-surface-sunken text-ink' : 'bg-brand-200 text-brand-800',
        sizes[size],
        className,
      )}
      {...props}
    >
      {src ? <BaseAvatar.Image src={src} alt={alt} className="size-full object-cover" /> : null}
      <BaseAvatar.Fallback className="flex size-full items-center justify-center">
        {fallback}
      </BaseAvatar.Fallback>
    </BaseAvatar.Root>
  );
}
